import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { mockArticles, categories } from '@/mocks/articles';

function getCategoryLabel(slug: string): string {
  const map: Record<string, string> = {
    'japan-drama': '日剧',
    'korea-drama': '韩剧',
    'japan-movie': '日本电影',
    'korea-movie': '韩国电影',
    'kpop': 'K-POP',
    'jpop': 'J-POP',
    'variety': '综艺',
    'star': '明星',
  };
  return map[slug] || slug;
}

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = mockArticles.find((a) => a.id === id);

  useEffect(() => {
    if (!article) return;

    const catName = getCategoryLabel(article.category);

    const articleSchema = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.excerpt,
      author: {
        '@type': 'Person',
        name: article.author,
      },
      datePublished: article.publishedAt,
      url: `https://aaa-rikhan.example.com/article/${article.id}`,
      publisher: {
        '@type': 'Organization',
        name: 'aaa一级日韩',
      },
    };

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: '首页',
          item: 'https://aaa-rikhan.example.com/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: catName,
          item: `https://aaa-rikhan.example.com/category/${article.category}`,
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: article.title,
        },
      ],
    };

    const scripts = [JSON.stringify(articleSchema), JSON.stringify(breadcrumbSchema)];
    scripts.forEach((s) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = s;
      document.head.appendChild(script);
    });

    return () => {
      document.head.querySelectorAll('script[type="application/ld+json"]').forEach((el) => {
        if (scripts.some((s) => el.textContent === s)) el.remove();
      });
    };
  }, [article]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16">
          <div className="max-w-[800px] mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-950 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">抱歉，您访问的文章不存在或已被删除。</p>
            <Link
              to="/"
              className="inline-block px-6 py-2.5 rounded-md bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const catName = getCategoryLabel(article.category);
  const relatedArticles = mockArticles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <article className="max-w-[800px] mx-auto px-4 md:px-6 py-8 md:py-10">
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6 flex-wrap">
            <Link to="/" className="hover:text-primary-600 transition-colors duration-200 cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line text-[10px]"></i>
            <Link to={`/category/${article.category}`} className="hover:text-primary-600 transition-colors duration-200 cursor-pointer">{catName}</Link>
            <i className="ri-arrow-right-s-line text-[10px]"></i>
            <span className="text-foreground-700 truncate max-w-[200px]">{article.title}</span>
          </nav>

          <span className="inline-block px-2.5 py-1 rounded-md text-xs font-medium bg-primary-50 text-primary-700 mb-4">
            {catName}
          </span>

          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4 leading-tight">
            {article.title}
          </h1>

          <div className="flex items-center gap-3 text-xs text-foreground-500 mb-6 flex-wrap">
            <span className="flex items-center gap-1">
              <i className="ri-user-line text-[11px]"></i>
              {article.author}
            </span>
            <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
            <span>{article.publishedAt}</span>
            <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
            <span>{article.readTime}</span>
            <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line text-[11px]"></i>
              {article.views.toLocaleString()} 阅读
            </span>
          </div>

          <div className="relative overflow-hidden rounded-lg aspect-[16/9] mb-8">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div
            className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-10 pt-6 border-t border-background-200/70">
            <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
              <span className="text-foreground-700 font-medium">标签：</span>
              <span className="px-2 py-1 rounded-full bg-background-100 cursor-pointer hover:bg-background-200 transition-colors duration-200">{catName}</span>
              <span className="px-2 py-1 rounded-full bg-background-100 cursor-pointer hover:bg-background-200 transition-colors duration-200">日韩娱乐</span>
              <span className="px-2 py-1 rounded-full bg-background-100 cursor-pointer hover:bg-background-200 transition-colors duration-200">aaa一级日韩</span>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="bg-background-100 border-t border-background-200/70">
            <div className="max-w-[800px] mx-auto px-4 md:px-6 py-10 md:py-14">
              <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-6">
                相关推荐
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <article key={ra.id} className="group cursor-pointer">
                    <Link to={`/article/${ra.id}`} className="block">
                      <div className="relative overflow-hidden rounded-md aspect-[16/10] mb-3">
                        <img
                          src={ra.coverImage}
                          alt={ra.title}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                      </div>
                      <h4 className="text-sm font-heading font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2">
                        {ra.title}
                      </h4>
                      <p className="text-xs text-foreground-500 mt-1">{ra.publishedAt}</p>
                    </Link>
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}